import interactWithUSComments from "../../../config/liveFirebase";

/**
 * Fetches quality data for a specific project
 * @param {string} projectId - The ID of the project to fetch quality data for
 * @returns {Promise<{data: Object, status: string}>} - Quality data response
 */
export const fetchQualityData = async (projectId) => {
  if (!projectId) {
    return {
      data: null,
      status: "Error: No project ID provided",
    };
  }

  try {
    // Fetch miscellaneous quality data
    const miscResponse = await interactWithUSComments(
      "GET",
      `${projectId}/miscellaneous`
    );

    // Fetch blocks data which contains other quality items
    const blocksResponse = await interactWithUSComments(
      "GET",
      `${projectId}/Blocks`
    );

    return {
      data: {
        miscellaneous: miscResponse.data || {},
        Blocks: blocksResponse.data || {},
      },
      status: "Success",
    };
  } catch (error) {
    console.error("Error fetching quality data:", error);
    return {
      data: null,
      status: "Error: " + (error.message || "Failed to fetch quality data"),
    };
  }
};

/**
 * Creates or updates a quality report item
 * @param {string} path - Firebase path for the quality item
 * @param {Object} qualityData - Quality report data to save
 * @returns {Promise<{data: Object, status: string}>} - Response status
 */
export const saveQualityItem = async (path, qualityData) => {
  if (!path || !qualityData) {
    return {
      data: null,
      status: "Error: Missing path or quality data",
    };
  }

  try {
    const response = await interactWithUSComments("POST", path, qualityData);
    return response;
  } catch (error) {
    console.error("Error saving quality item:", error);
    return {
      data: null,
      status: "Error: " + (error.message || "Failed to save quality data"),
    };
  }
};

/**
 * Deletes a quality report item
 * @param {string} path - Firebase path for the quality item to delete
 * @returns {Promise<{data: null, status: string}>} - Response status
 */
export const deleteQualityItem = async (path) => {
  if (!path) {
    return {
      data: null,
      status: "Error: No path provided",
    };
  }

  try {
    const response = await interactWithUSComments("DEL", path);
    return response;
  } catch (error) {
    console.error("Error deleting quality item:", error);
    return {
      data: null,
      status: "Error: " + (error.message || "Failed to delete quality item"),
    };
  }
};

/**
 * Processes raw quality data from Firebase into a structured format grouped by date
 * @param {Object} projectData - Raw project data from Firebase
 * @param {string} projectId - Project ID
 * @returns {Object} Formatted quality data grouped by date
 */
export const processQualityData = (projectData, projectId) => {
  try {
    if (!projectData) {
      return {};
    }

    const formattedData = {};

    const processEntries = (entries, level, pathTemplate) => {
      for (const [dateStamp, data] of Object.entries(entries)) {
        const qualityItems = data.quality;
        if (!qualityItems) continue;

        for (const [timestamp, qualityItem] of Object.entries(qualityItems)) {
          const dateKey = new Date(parseInt(timestamp)).setHours(0, 0, 0, 0);

          if (!formattedData[dateKey]) {
            formattedData[dateKey] = {
              id: dateKey.toString(),
              date: getFormattedDate(dateKey),
              items: [],
            };
          }

          formattedData[dateKey].items.push({
            ...qualityItem,
            id: timestamp,
            level,
            type: "item",
            path:
              qualityItem.path ||
              pathTemplate
                .replace("{dateStamp}", dateStamp)
                .replace("{timestamp}", timestamp),
          });
        }
      }
    };

    if (projectData.miscellaneous) {
      processEntries(
        projectData.miscellaneous,
        "Miscellaneous",
        `${projectId}/miscellaneous/{dateStamp}/quality/{timestamp}`
      );
    }

    if (projectData.Blocks) {
      for (const [blockIndex, block] of Object.entries(projectData.Blocks)) {
        if (!block?.name) continue;

        if (block.BlockImages) {
          processEntries(
            block.BlockImages,
            "Block",
            `${projectId}/Blocks/${blockIndex}/BlockImages/{dateStamp}/quality/{timestamp}`
          );
        }

        if (block.Images) {
          for (const [floorKey, floorData] of Object.entries(block.Images)) {
            if (floorData.FloorImages) {
              processEntries(
                floorData.FloorImages,
                "Floor",
                `${projectId}/Blocks/${blockIndex}/Images/${floorKey}/FloorImages/{dateStamp}/quality/{timestamp}`
              );
            }

            for (const [dateStamp, points] of Object.entries(floorData)) {
              if (!dateStamp.startsWith("dateStamp:")) continue;

              for (const [pointKey, pointData] of Object.entries(points)) {
                if (!pointKey.startsWith("pointNumber") || !pointData?.quality)
                  continue;

                processEntries(
                  { [dateStamp]: pointData },
                  "Point",
                  `${projectId}/Blocks/${blockIndex}/Images/${floorKey}/{dateStamp}/${pointKey}/quality/{timestamp}`
                );
              }
            }
          }
        }
      }
    }

    // Sort data by date (newest first) and sort items by timestamp (newest first)
    const sortedData = Object.fromEntries(
      Object.entries(formattedData)
        .sort(([a], [b]) => parseInt(b) - parseInt(a))
        .map(([key, value]) => [
          key,
          {
            ...value,
            items: value.items.sort((a, b) => parseInt(b.id) - parseInt(a.id)),
          },
        ])
    );

    return sortedData;
  } catch (error) {
    console.error("Error processing quality data:", error);
    return {};
  }
};

/**
 * Helper function to format date in a human-readable way
 * @param {number} timestamp - Date timestamp
 * @returns {string} Formatted date string
 */
const getFormattedDate = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date().setHours(0, 0, 0, 0);
  const yesterday = today - 86400000;

  if (date.setHours(0, 0, 0, 0) === today) {
    return "Today";
  } else if (date.setHours(0, 0, 0, 0) === yesterday) {
    return "Yesterday";
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * Helper function to filter quality data based on tab, search, and location
 * @param {Object} qualityData - Processed quality data
 * @param {string} selectedTab - Selected tab filter (All/Opened/Closed)
 * @param {string} searchQuery - Search query text
 * @param {string} location - Selected location filter
 * @returns {Array} Filtered quality data items with headers
 */
export const getFilteredQualityData = (
  qualityData,
  selectedTab = "All",
  searchQuery = "",
  location = ""
) => {
  if (!qualityData || Object.keys(qualityData).length === 0) return [];

  const searchTerm = searchQuery.toLowerCase();
  const filteredItems = [];

  // Process data for each date group
  Object.values(qualityData).forEach((dateGroup) => {
    const { date, items } = dateGroup;

    // Filter items based on criteria
    const matchingItems = items.filter((item) => {
      // Filter by tab (All, Open, Closed)
      const statusMatches =
        selectedTab === "All" ||
        (selectedTab === "Opened" && item.status?.toLowerCase() === "open") ||
        (selectedTab === "Closed" && item.status?.toLowerCase() === "closed");

      // Filter by search term
      const matchesSearch =
        !searchTerm ||
        ["location", "description", "team", "by", "blockName"].some((key) =>
          item[key]?.toLowerCase().includes(searchTerm)
        );

      // Filter by location
      const locationMatches = !location || item.location === location;

      return statusMatches && matchesSearch && locationMatches;
    });

    // Only add date header if there are items for this date
    if (matchingItems.length > 0) {
      filteredItems.push({ id: `header-${date}`, type: "header", date });
      filteredItems.push(...matchingItems);
    }
  });

  return filteredItems;
};

/**
 * Sets up a real-time listener for quality data changes
 * @param {string} projectId - The ID of the project to watch
 * @param {Function} onUpdate - Callback function when data updates
 * @param {Function} onProgress - Callback to report loading progress (0-100)
 * @param {Function} onItemProcessed - Callback when a single item is processed
 * @returns {Object} An object with methods to manage the listener
 */
export const setupQualityDataListener = (
  projectId,
  onUpdate,
  onProgress,
  onItemProcessed
) => {
  if (!projectId) {
    onProgress && onProgress(100, { error: "No project ID provided" });
    return {
      unsubscribe: () => {},
      isActive: () => false,
    };
  }

  let isListening = true;
  let progress = { misc: 0, blocks: 0 };
  let collectedData = {};
  let lastUpdate = Date.now();
  const POLL_INTERVAL = 3000; // Poll every 3 seconds
  const ERROR_RETRY_INTERVAL = 10000; // Retry after 10s on error

  // Update the overall progress
  const updateProgress = () => {
    const total = (progress.misc + progress.blocks) / 2;
    onProgress && onProgress(Math.round(total));
  };

  // Process a single issue item and notify listeners
  const processIssueItem = (
    dateStamp,
    timestamp,
    item,
    level,
    pathTemplate
  ) => {
    const dateKey = new Date(parseInt(timestamp)).setHours(0, 0, 0, 0);

    if (!collectedData[dateKey]) {
      collectedData[dateKey] = {
        id: dateKey.toString(),
        date: getFormattedDate(dateKey),
        items: [],
      };
    }

    // Check if the item already exists and compare for changes
    const existingItemIndex = collectedData[dateKey].items.findIndex(
      (existing) => existing.id === timestamp
    );

    const processedItem = {
      ...item,
      id: timestamp,
      level,
      type: "item",
      path:
        item.path ||
        pathTemplate
          .replace("{dateStamp}", dateStamp)
          .replace("{timestamp}", timestamp),
    };

    let itemUpdated = false;

    if (existingItemIndex !== -1) {
      // Item already exists, check if it changed
      const existingItem = collectedData[dateKey].items[existingItemIndex];
      if (JSON.stringify(existingItem) !== JSON.stringify(processedItem)) {
        // Replace the item with updated version
        collectedData[dateKey].items[existingItemIndex] = processedItem;
        itemUpdated = true;
      }
    } else {
      // New item, add it
      collectedData[dateKey].items.push(processedItem);
      itemUpdated = true;
    }

    // Call the item processed callback only if it's a new or updated item
    if (itemUpdated && onItemProcessed) {
      onItemProcessed({
        dateKey,
        dateHeader: {
          id: `header-${dateKey}`,
          type: "header",
          date: getFormattedDate(dateKey),
        },
        item: processedItem,
      });

      // Mark that we had an update
      lastUpdate = Date.now();
    }
  };

  // Process entries with delays between items
  const processEntriesAsync = async (
    entries,
    level,
    pathTemplate,
    isInitial = false
  ) => {
    // First collect all items to process for better sequencing
    const itemsToProcess = [];

    // Gather all quality items into a flat array for processing
    for (const [dateStamp, data] of Object.entries(entries)) {
      const qualityItems = data.quality;
      if (!qualityItems) continue;

      // Add each item to our processing queue
      Object.entries(qualityItems).forEach(([timestamp, qualityItem]) => {
        itemsToProcess.push({
          dateStamp,
          timestamp,
          qualityItem,
          level,
          pathTemplate,
        });
      });
    }

    // Sort by timestamp to show newest first (this gives a better UX during loading)
    itemsToProcess.sort(
      (a, b) => parseInt(b.timestamp) - parseInt(a.timestamp)
    );

    // Process items with visual staggering effect
    for (let i = 0; i < itemsToProcess.length; i++) {
      const { dateStamp, timestamp, qualityItem, level, pathTemplate } =
        itemsToProcess[i];

      // Process this item immediately to update the UI
      processIssueItem(dateStamp, timestamp, qualityItem, level, pathTemplate);

      // Add a small delay between items, with longer delays for initial load
      // to create a smooth progressive loading effect that users can follow
      if (i < itemsToProcess.length - 1) {
        // During initial load, use longer delays for visual effect
        // For refreshes or updates, use minimal delays
        const delayTime = isInitial
          ? Math.min(60, 150 - i * 2) // Progressive delay that gets shorter as more items load
          : 10; // Very minimal delay for updates/refreshes

        await new Promise((resolve) => setTimeout(resolve, delayTime));
      }
    }
  };

  // Process and combine the data for final update (keeping track of full dataset)
  const finalizeDataUpdate = () => {
    if (progress.misc < 100 || progress.blocks < 100) return;

    try {
      // Sort data by date (newest first) and sort items by timestamp (newest first)
      const sortedData = Object.fromEntries(
        Object.entries(collectedData)
          .sort(([a], [b]) => parseInt(b) - parseInt(a))
          .map(([key, value]) => [
            key,
            {
              ...value,
              items: value.items.sort(
                (a, b) => parseInt(b.id) - parseInt(a.id)
              ),
            },
          ])
      );

      // Only send update if there's been a change since last update
      onUpdate && onUpdate(sortedData);
    } catch (error) {
      console.error("Error finalizing quality data:", error);
    }
  };

  // Define setupMiscListener and setupBlocksListener at the top level of setupQualityDataListener
  const setupMiscListener = async () => {
    progress.misc = 30;
    updateProgress();

    // Get initial data
    const miscResponse = await interactWithUSComments(
      "GET",
      `${projectId}/miscellaneous`
    );

    if (miscResponse.data) {
      progress.misc = 50;
      updateProgress();

      // Process misc items one by one with a delay
      await processEntriesAsync(
        miscResponse.data,
        "Miscellaneous",
        `${projectId}/miscellaneous/{dateStamp}/quality/{timestamp}`,
        true // isInitial = true for first load
      );
    }

    progress.misc = 70;
    updateProgress();

    // Setup ongoing listener
    const pollMiscChanges = () => {
      if (!isListening) return;

      interactWithUSComments("GET", `${projectId}/miscellaneous`)
        .then(async (response) => {
          if (isListening) {
            progress.misc = 100;
            updateProgress();

            if (response.data) {
              // Process any new items - no delay for updates
              await processEntriesAsync(
                response.data,
                "Miscellaneous",
                `${projectId}/miscellaneous/{dateStamp}/quality/{timestamp}`,
                false
              );
            }

            finalizeDataUpdate();

            // Schedule next poll
            setTimeout(pollMiscChanges, POLL_INTERVAL);
          }
        })
        .catch((error) => {
          console.error("Error polling misc data:", error);
          if (isListening) {
            setTimeout(pollMiscChanges, ERROR_RETRY_INTERVAL);
          }
        });
    };

    pollMiscChanges();
  };

  const setupBlocksListener = async () => {
    progress.blocks = 30;
    updateProgress();

    // Get initial data
    const blocksResponse = await interactWithUSComments(
      "GET",
      `${projectId}/Blocks`
    );

    if (blocksResponse.data) {
      progress.blocks = 50;
      updateProgress();

      // Process blocks data
      for (const [blockIndex, block] of Object.entries(blocksResponse.data)) {
        if (!block?.name) continue;

        if (block.BlockImages) {
          await processEntriesAsync(
            block.BlockImages,
            "Block",
            `${projectId}/Blocks/${blockIndex}/BlockImages/{dateStamp}/quality/{timestamp}`,
            true
          );
        }

        if (block.Images) {
          for (const [floorKey, floorData] of Object.entries(block.Images)) {
            if (floorData.FloorImages) {
              await processEntriesAsync(
                floorData.FloorImages,
                "Floor",
                `${projectId}/Blocks/${blockIndex}/Images/${floorKey}/FloorImages/{dateStamp}/quality/{timestamp}`,
                true
              );
            }

            for (const [dateStamp, points] of Object.entries(floorData)) {
              if (!dateStamp.startsWith("dateStamp:")) continue;

              for (const [pointKey, pointData] of Object.entries(points)) {
                if (!pointKey.startsWith("pointNumber") || !pointData?.quality)
                  continue;

                await processEntriesAsync(
                  { [dateStamp]: pointData },
                  "Point",
                  `${projectId}/Blocks/${blockIndex}/Images/${floorKey}/{dateStamp}/${pointKey}/quality/{timestamp}`,
                  true
                );
              }
            }
          }
        }
      }
    }

    progress.blocks = 70;
    updateProgress();

    // Setup ongoing listener for blocks
    const pollBlocksChanges = () => {
      if (!isListening) return;

      interactWithUSComments("GET", `${projectId}/Blocks`)
        .then(async (response) => {
          if (isListening) {
            progress.blocks = 100;
            updateProgress();

            if (response.data) {
              // Process blocks data - no delay for updates
              for (const [blockIndex, block] of Object.entries(response.data)) {
                if (!block?.name) continue;

                if (block.BlockImages) {
                  await processEntriesAsync(
                    block.BlockImages,
                    "Block",
                    `${projectId}/Blocks/${blockIndex}/BlockImages/{dateStamp}/quality/{timestamp}`,
                    false
                  );
                }

                if (block.Images) {
                  for (const [floorKey, floorData] of Object.entries(
                    block.Images
                  )) {
                    if (floorData.FloorImages) {
                      await processEntriesAsync(
                        floorData.FloorImages,
                        "Floor",
                        `${projectId}/Blocks/${blockIndex}/Images/${floorKey}/FloorImages/{dateStamp}/quality/{timestamp}`,
                        false
                      );
                    }

                    for (const [dateStamp, points] of Object.entries(
                      floorData
                    )) {
                      if (!dateStamp.startsWith("dateStamp:")) continue;

                      for (const [pointKey, pointData] of Object.entries(
                        points
                      )) {
                        if (
                          !pointKey.startsWith("pointNumber") ||
                          !pointData?.quality
                        )
                          continue;

                        await processEntriesAsync(
                          { [dateStamp]: pointData },
                          "Point",
                          `${projectId}/Blocks/${blockIndex}/Images/${floorKey}/{dateStamp}/${pointKey}/quality/{timestamp}`,
                          false
                        );
                      }
                    }
                  }
                }
              }
            }

            finalizeDataUpdate();

            // Schedule next poll
            setTimeout(pollBlocksChanges, POLL_INTERVAL);
          }
        })
        .catch((error) => {
          console.error("Error polling blocks data:", error);
          if (isListening) {
            setTimeout(pollBlocksChanges, ERROR_RETRY_INTERVAL);
          }
        });
    };

    pollBlocksChanges();
  };

  // Set up listener for miscellaneous data
  try {
    onProgress && onProgress(0);

    // Start both listeners
    setupMiscListener();
    setupBlocksListener();
  } catch (error) {
    console.error("Error setting up quality data listeners:", error);
    onProgress && onProgress(100, { error: error.message });
  }

  // Return control methods
  return {
    unsubscribe: () => {
      isListening = false;
    },
    isActive: () => isListening,
    refresh: () => {
      // Force a refresh if needed
      if (isListening) {
        setupMiscListener();
        setupBlocksListener();
      }
    },
  };
};
